import React, { useEffect, useState, useRef } from 'react';
import Invoice from './invoice';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { getNewRequest, postPayURequest } from '../../Utils/AppApi';
import { BBPS_IMWALLET_TRANSACTION_HISTORY, BBPS_IMWALLET_TRANSACTION_STATUS } from '../../Utils/appConstants';
import { useHistory } from "react-router-dom";
import { Popconfirm, Tooltip, Modal, Select, DatePicker, message } from 'antd';
import { ReloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { getUTCToLocalDateTimeFormat, getExportFormat } from '../../Utils/function';
import moment from 'moment';

function History() {
    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;
    const dateFormat = 'DD-MM-YYYY';
    const MAX_EXPORT_DAYS = 7;

    // ✅ Always use DD-MM-YYYY strings for dates
    const todayStr = moment().format(dateFormat);

    const [getData, setData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isInvoiceModal, setInvoiceModal] = useState(false);
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isExporting, setExporting] = useState(false);
    const [csvData, setCsvData] = useState([]);
    const [fileName, setFileName] = useState('bbps_transaction_history');

    const [filters, setFilters] = useState({
        startDate: todayStr,
        endDate: todayStr,
        searchBy: '',
        searchValue: ''
    });

    // ---------- Helpers ----------
    const viewInvoice = (row) => {
        setSelectedTransactionId(row.txtId);
        setInvoiceModal(true);
    };

    const objectToQueryString = (obj) => {
        const str = [];
        for (const p in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, p) && obj[p]) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        }
        return str.join("&");
    };

    // Returns a safe DD-MM-YYYY string, or '' when invalid/missing
    const safeDate = (dateStr) => {
        if (!dateStr) return '';
        const m = moment(dateStr, dateFormat, true);
        return m.isValid() ? m.format(dateFormat) : '';
    };

    // ---------- Fetch table data ----------
    const getDataReports = async () => {
        setLoading(true);

        const params = {
            page: page,
            limit: limit,
            startDate: safeDate(filters.startDate),
            endDate: safeDate(filters.endDate),
            searchBy: filters.searchBy,
            search: filters.searchValue
        };
        const queryString = objectToQueryString(params);

        try {
            const response = await getNewRequest(
                `${BBPS_IMWALLET_TRANSACTION_HISTORY}?${queryString}`,
                history
            );

            if (response.success === true) {
                setData(response.data?.docs || []);
                setTotalRecord(response.data?.totalDocs || 0);
                setPage(response.data?.page || 1);
            } else {
                // ✅ Show backend error message
                setData([]);
                setTotalRecord(0);
                setPage(1);
                message.error(response.message || 'Failed to load history');
            }
        } catch (error) {
            console.error(error);
            // ✅ Show thrown error or fallback
            message.error(
                error?.response?.data?.message ||
                error?.message ||
                'Failed to load history'
            );
        } finally {
            setLoading(false);
        }
    };

    // ---------- Export helpers ----------
    const generateFileName = () => {
        const from = safeDate(filters.startDate);
        const to = safeDate(filters.endDate);
        if (from && to) {
            return `bbps_transaction_history_${from}_to_${to}`;
        }
        return 'bbps_transaction_history';
    };

    const validateExportRange = () => {
        if (!filters.startDate || !filters.endDate) {
            message.error('Please select From and To dates');
            return false;
        }

        const start = moment(filters.startDate, dateFormat, true);
        const end = moment(filters.endDate, dateFormat, true);

        if (!start.isValid() || !end.isValid()) {
            message.error('Invalid date selection');
            return false;
        }

        if (end.isBefore(start, 'day')) {
            message.error('"To" date cannot be before "From" date');
            return false;
        }

        const diffDays = end.diff(start, 'days') + 1; // inclusive
        if (diffDays > MAX_EXPORT_DAYS) {
            message.error(`Export range cannot exceed ${MAX_EXPORT_DAYS} days. Selected: ${diffDays} days.`);
            return false;
        }

        return true;
    };

    // ---------- Export CSV ----------
    const getCSVDataList = () => {
        if (!validateExportRange()) return;

        setExporting(true);
        const params = {
            exportsCsv: true,
            limit: 1000000,
            startDate: safeDate(filters.startDate),
            endDate: safeDate(filters.endDate),
            searchBy: filters.searchBy,
            search: filters.searchValue
        };
        const queryString = objectToQueryString(params);

        getNewRequest(`${BBPS_IMWALLET_TRANSACTION_HISTORY}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    const records = response.data?.docs || [];
                    const csvRows = records.map((row) => ({
                        'Date & Time': getExportFormat(row.createdAt),
                        'TXT ID': row.txtId,
                        'Customer Name': row.customerName,
                        'Account Number': row.accountNumber,
                        'Biller Name': row.billerName,
                        'Category': row.category,
                        'Mobile': row.customerPhoneNumber,
                        'Amount': row.paidAmount ? row.paidAmount : '-',
                        'Status': row.txnStatus?.replace(/_/g, ' ') || 'N/A'
                    }));

                    if (!csvRows.length) {
                        message.warning('No records found for the selected filters');
                        setExporting(false);
                        return;
                    }

                    setCsvData(csvRows);
                    setFileName(generateFileName());

                    setTimeout(() => {
                        if (csvLink.current) csvLink.current.link.click();
                        setExporting(false);
                    }, 500);
                } else {
                    // ✅ Show backend message
                    message.error(response.message || 'Failed to export data');
                    setExporting(false);
                }
            })
            .catch((error) => {
                console.error(error);
                message.error(
                    error?.response?.data?.message ||
                    error?.message ||
                    'Error exporting data'
                );
                setExporting(false);
            });
    };

    // ---------- UI handlers ----------
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleStatusCheck = async (refId) => {
        try {
            const response = await postPayURequest(BBPS_IMWALLET_TRANSACTION_STATUS, { refId }, history);
            if (response.success === true) {
                message.success(response.message || 'Status updated');
                getDataReports();
            } else {
                // ✅ Already correct here
                message.error(response.message || 'Failed to update status');
            }
        } catch (error) {
            console.error(error);
            message.error(
                error?.response?.data?.message ||
                error?.message ||
                'Error checking status'
            );
        }
    };

    const handleSearch = () => {
        setPage(1);
        getDataReports();
    };

    const handleClear = () => {
        setFilters({
            startDate: todayStr,
            endDate: todayStr,
            searchBy: '',
            searchValue: ''
        });
        setPage(1);
        setTimeout(() => getDataReports(), 100);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        getDataReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // ---------- Table columns ----------
    const columns = [
        {
            name: 'Date & Time',
            selector: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "160px"
        },
        {
            name: 'TXT ID',
            selector: row => <Tooltip title={row.txtId}>{row.txtId}</Tooltip>,
            width: "180px"
        },
        {
            name: 'Customer Name',
            selector: row => <Tooltip title={row.customerName}>{row.customerName}</Tooltip>,
            width: "180px"
        },
        {
            name: 'Account Number',
            selector: row => <Tooltip title={row.accountNumber}>{row.accountNumber}</Tooltip>,
            width: "160px"
        },
        {
            name: 'Biller Name',
            selector: row => <Tooltip title={row.billerName}>{row.billerName}</Tooltip>,
            width: "200px"
        },
        {
            name: 'Category',
            selector: row => <Tooltip title={row.category}>{row.category}</Tooltip>,
            width: "120px"
        },
        {
            name: 'Mobile',
            selector: row => <Tooltip title={row.customerPhoneNumber}>{row.customerPhoneNumber}</Tooltip>,
            width: "140px"
        },
        {
            name: 'Amount',
            selector: row => row.paidAmount ? `₹${row.paidAmount}` : "-",
            width: "100px"
        },
        {
            name: 'Status',
            cell: row => {
                let color = '';
                if (row.txnStatus === 'PAYMENT_SUCCESS') color = 'green';
                else if (row.txnStatus === 'PENDING') color = 'orange';
                else if (row.txnStatus === 'PAYMENT_FAILURE') color = 'red';
                else color = 'gray';
                return <span style={{ color, fontWeight: 500 }}>{row.txnStatus?.replace(/_/g, ' ') || 'N/A'}</span>;
            },
            width: "140px"
        },
        {
            name: 'Action',
            cell: row => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    {row.txnStatus === 'PENDING' && (
                        <Popconfirm
                            title="Check transaction status?"
                            onConfirm={() => handleStatusCheck(row.refId)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Tooltip title="Check Status">
                                <div className="icon-border border border-warning text-warning pointer">
                                    <ReloadOutlined />
                                </div>
                            </Tooltip>
                        </Popconfirm>
                    )}

                    <Tooltip title="View Invoice">
                        <div
                            className="icon-border border border-success text-success pointer"
                            onClick={() => viewInvoice(row)}
                        >
                            <FileTextOutlined />
                        </div>
                    </Tooltip>
                </div>
            ),
            width: "120px"
        }
    ];

    const printStyles = `
        @media print {
            .ant-modal-mask, .ant-modal-wrap, .ant-modal-header, .ant-modal-footer {
                display: none !important;
            }
            .ant-modal-content {
                background: white !important;
                box-shadow: none !important;
                padding: 0 !important;
            }
            .ant-modal-body {
                padding: 0 !important;
            }
        }
    `;

    return (
        <div>
            <style>{printStyles}</style>

            <div className="row">
                <div className='col-md-12 date-search mb-0'>
                    <div className='row'>
                        {/* From */}
                        <div className='col-md-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-blue'>From</label>
                                <DatePicker
                                    className="form-control form-control-border"
                                    format={dateFormat}
                                    value={filters.startDate ? moment(filters.startDate, dateFormat) : null}
                                    onChange={(date, dateString) => handleFilterChange('startDate', dateString)}
                                />
                            </div>
                        </div>

                        {/* To */}
                        <div className='col-md-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-red'>To</label>
                                <DatePicker
                                    className="form-control form-control-border"
                                    format={dateFormat}
                                    value={filters.endDate ? moment(filters.endDate, dateFormat) : null}
                                    onChange={(date, dateString) => handleFilterChange('endDate', dateString)}
                                />
                            </div>
                        </div>

                        {/* Search By */}
                        <div className='col-md-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-blue'>&nbsp;</label>
                                <Select
                                    options={[
                                        { label: "Transaction ID", value: "ID" },
                                        { label: "Account Number", value: "account_number" },
                                        { label: "Mobile", value: "mobile" }
                                    ]}
                                    className="normal-light w-100 form-control-border"
                                    placeholder="Search By"
                                    value={filters.searchBy || undefined}
                                    onChange={(value) => handleFilterChange('searchBy', value)}
                                    allowClear
                                />
                            </div>
                        </div>

                        {/* Search input */}
                        <div className='col-md-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-blue'>&nbsp;</label>
                                <input
                                    type="text"
                                    className="form-control form-control-border"
                                    placeholder="Search..."
                                    value={filters.searchValue}
                                    onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Search + Export buttons */}
                        <div className='col-md-4'>
                            <div className="form-group">
                                <label className='date-search-label mb-0'>&nbsp;</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        type="button"
                                        className="btn btn-primary rounded-pill pl-4 pr-4"
                                        onClick={handleSearch}
                                    >
                                        Search
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success rounded-pill pl-4 pr-4"
                                        onClick={getCSVDataList}
                                        disabled={isExporting}
                                    >
                                        <i className="fa fa-file-excel mr-1"></i>
                                        {isExporting ? 'Exporting...' : 'Export'}
                                    </button>
                                </div>
                                <CSVLink
                                    data={csvData}
                                    filename={fileName}
                                    className='hidden'
                                    ref={csvLink}
                                    target='_blank'
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='container-fluid history-table'>
                <div className="pb-5">
                    <DataTable
                        title="BBPS Transaction History"
                        columns={columns}
                        data={getData}
                        highlightOnHover
                        pagination
                        paginationServer
                        paginationTotalRows={getTotalRecord}
                        onChangeRowsPerPage={getDataReports}
                        onChangePage={handlePageChange}
                        progressPending={loading}
                    />
                </div>
            </div>

            <Modal
                title={null}
                width={850}
                open={isInvoiceModal}
                footer={false}
                closable={true}
                onCancel={() => setInvoiceModal(false)}
                bodyStyle={{ padding: 0 }}
                maskClosable={true}
            >
                <Invoice transactionId={selectedTransactionId} />
            </Modal>
        </div>
    );
}

export default History;